import React, { useState, useEffect, useRef } from 'react';
import './FullAbout.css';

const FullAbout = () => {
    const [activeFaq, setActiveFaq] = useState(0);
    const [activeYear, setActiveYear] = useState(0);
    const journeyRef = useRef(null);

    const faqs = [
        {
            q: "What machining services do you provide?",
            a: "We offer CNC machining, VMC machining, drilling, milling, and metal cutting services for industrial components."
        },
        {
            q: "What materials do you work with?",
            a: "We machine various metals including mild steel, stainless steel, aluminum, and other engineering-grade materials based on client requirements."
        },
        {
            q: "Can you handle close tolerance machining?",
            a: "Yes, we specialize in precision machining with strict dimensional tolerances to meet industrial and engineering standards."
        },
        {
            q: "Do you accept custom component manufacturing?",
            a: "Yes, we manufacture components based on customer drawings, samples, or technical specifications."
        },
        {
            q: "What industries do you serve?",
            a: "We support manufacturing, automotive, engineering, and industrial equipment sectors."
        },
        {
            q: "What is your typical project turnaround time?",
            a: "Turnaround time depends on component complexity and quantity. We focus on timely delivery while maintaining quality standards."
        }
    ];

    const [scrollProgress, setScrollProgress] = useState(0);
    const stickyContainerRef = useRef(null);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        handleResize(); // Initialize on mount
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const timeline = [
        { year: '2006', title: 'Establishment', text: 'Sakthivel Engineering was established in Ambattur, Chennai, with a vision to provide precision CNC and machining services for industrial applications.', image: 'https://sakthivel.ascentbw.com/Files/About/2006.webp' },
        { year: '2020', title: 'Expansion', text: 'Expanded our facility and added more advanced VMC machines to meet growing demand.', image: 'https://sakthivel.ascentbw.com/Files/About/2020.webp' },
        { year: '2022', title: 'Quality Focus', text: 'Implemented rigorous quality standards and received industry certifications.', image: 'https://sakthivel.ascentbw.com/Files/About/2022.webp' },
        { year: '2024', title: 'Advanced Tech', text: 'Invested in high-precision automated systems for faster turnaround.', image: 'https://sakthivel.ascentbw.com/Files/About/2024.webp' },
        { year: '2025', title: 'Global Standards', text: 'Supporting larger manufacturing clusters with enterprise-grade solutions.', image: 'https://sakthivel.ascentbw.com/Files/About/2025.webp' },
        { year: 'Present', title: 'Innovation', text: 'Continuing to innovate in precision engineering and client delivery.', image: 'https://sakthivel.ascentbw.com/Files/About/present.webp' }
    ];

    useEffect(() => {
        const handleScroll = () => {
            if (!journeyRef.current || !stickyContainerRef.current) return;
            const { top, height } = journeyRef.current.getBoundingClientRect();
            
            const stickyTop = 80; // Assuming sticky offset is 80px for navbar
            const stickyElementHeight = stickyContainerRef.current.offsetHeight;
            
            // The exact scrollable pixel distance while the section remains perfectly frozen
            const scrollableDistance = height - stickyElementHeight;
            
            if (scrollableDistance <= 0) return;
            
            let progress = (stickyTop - top) / scrollableDistance;
            progress = Math.max(0, Math.min(1, progress));
            setScrollProgress(progress);
            
            const newIndex = Math.min(timeline.length - 1, Math.floor(progress * timeline.length));
            if (newIndex !== activeYear) {
                setActiveYear(newIndex);
            }
        };

        window.addEventListener('scroll', handleScroll);
        // Ensure DOM has painted before running initial calculation
        setTimeout(() => handleScroll(), 50);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [activeYear, timeline.length]);

    const handleDotClick = (idx) => {
        setActiveYear(idx);
        if (journeyRef.current && stickyContainerRef.current) {
            const { top, height } = journeyRef.current.getBoundingClientRect();
            const stickyElementHeight = stickyContainerRef.current.offsetHeight;
            const scrollableDistance = height - stickyElementHeight;
            
            const targetProgress = idx / (timeline.length - 1);
            const targetScrollY = window.scrollY + top - 80 + (targetProgress * scrollableDistance);
            
            window.scrollTo({
                top: targetScrollY,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className="full-about">
            {/* About Hero */}
            <section className="about-hero">
                <div className="container">
                    <h1 className="fade-in">About Sakthivel Engineering</h1>
                </div>
            </section>

            {/* Who We Are */}
            <section className="who-we-are section-padding">
                <div className="container">
                    <div className="about-split-grid">
                        <div className="about-img-frame">
                            <img src="https://sakthivel.ascentbw.com/Files/About/About1.png" alt="Machining Process" />
                            <div className="img-glow"></div>
                        </div>
                        <div className="about-text-content">
                            <h2 className="section-title"><span className="highlight">Who We Are</span></h2>
                            <p>Sakthivel Engineering is a precision machining company based in Ambattur, Chennai, specializing in CNC, VMC, drilling, milling, and metal cutting services. We manufacture high-quality industrial components designed to meet strict dimensional tolerances and engineering standards. Our focus on accuracy, reliability, and timely delivery makes us a dependable machining partner for manufacturing industries.</p>
                            <p>Driven by quality and precision, we collaborate with clients to deliver components that meet their exact technical specifications and production demands.</p>
                            <button className="btn-primary">View Our Services</button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Precision Commitment */}
            <section className="precision-commitment section-padding bg-light">
                <div className="container">
                    <div className="about-split-grid reverse">
                        <div className="commitment-text">
                            <h2 className="section-title">Precision Machining with Reliability and<br /> Commitment</h2>                            <div className="commitment-features">
                                <div className="feature-item">
                                    <span className="dot"></span>
                                    <p>Precision CNC & VMC Machining</p>
                                </div>
                                <div className="feature-item">
                                    <span className="dot"></span>
                                    <p>Tight Tolerance Manufacturing</p>
                                </div>
                                <div className="feature-item">
                                    <span className="dot"></span>
                                    <p>On-Time Project Delivery</p>
                                </div>
                                <div className="feature-item">
                                    <span className="dot"></span>
                                    <p>Industrial Component Expertise</p>
                                </div>
                            </div>
                        </div>
                        <div className="commitment-img-frame">
                            <img src="https://sakthivel.ascentbw.com/Files/About/About2.png" alt="Machining Commitment" />
                        </div>
                    </div>
                </div>
            </section>

            {isMobile ? (
                /* Mobile Normal Stacked Layout */
                <section className="our-journey section-padding">
                    <div className="container">
                        <h2 className="section-title">Our Journey</h2>
                        <p className="section-subtitle1 pt-3">Since our establishment, Sakthivel Engineering has steadily grown by focusing on precision <br />machining, quality standards, and reliable client support in the manufacturing sector.</p>
                        
                        <div className="mobile-timeline-container" style={{ marginTop: '40px', display: 'flex', flexDirection: 'column', gap: '40px' }}>
                            {timeline.map((item, idx) => (
                                <div key={idx} className="mobile-timeline-item">
                                    <div className="mobile-year-header" style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                                        <span style={{ background: 'var(--primary)', color: '#fff', padding: '5px 15px', borderRadius: '20px', fontSize: '18px', fontWeight: 'bold' }}>{item.year}</span>
                                        <h3 style={{ fontSize: '24px', margin: 0, color: '#1a1a1a' }}>{item.title}</h3>
                                    </div>
                                    <div className="journey-detail-card" style={{ padding: '20px' }}>
                                        <div className="journey-img" style={{ height: 'auto', marginBottom: '20px' }}>
                                            <img src={item.image} alt={item.title} style={{ borderRadius: '15px', width: '100%', height: '200px', objectFit: 'cover' }} />
                                        </div>
                                        <div className="journey-text">
                                            <p style={{ margin: 0, lineHeight: '1.6' }}>{item.text}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            ) : (
                /* Our Journey (Perfect Sticky Scroll-Jacking Layout - Desktop Only) */
                <div ref={journeyRef} style={{ height: '600vh', position: 'relative' }}>
                    <div ref={stickyContainerRef} style={{ position: 'sticky', top: '80px', overflow: 'hidden' }}>
                        <section className="our-journey section-padding" style={{ paddingBottom: '30px' }}>
                        <div className="container">
                            <h2 className="section-title">Our Journey</h2>
                            <p className="section-subtitle1 pt-3">Since our establishment, Sakthivel Engineering has steadily grown by focusing on precision <br />machining, quality standards, and reliable client support in the manufacturing sector.</p>

                            <div className="timeline-container">
                                <div className="timeline-line"></div>
                                <div className="timeline-items">
                                    {timeline.map((item, idx) => (
                                        <div key={idx} 
                                            className={`timeline-dot ${activeYear === idx ? 'active' : ''}`}
                                            onClick={() => handleDotClick(idx)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <span className="year">{item.year}</span>
                                            <span className="dot-marker"></span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div style={{ overflow: 'hidden', width: '100%', paddingBottom: '10px' }}>
                                <div style={{ 
                                    display: 'flex', 
                                    width: `${timeline.length * 100}%`, 
                                    transform: `translateX(-${scrollProgress * ((timeline.length - 1) / timeline.length) * 100}%)`, 
                                    transition: 'transform 0.1s ease-out'
                                }}>
                                    {timeline.map((item, idx) => (
                                        <div key={idx} style={{ 
                                            width: `${100 / timeline.length}%`, 
                                            paddingRight: '20px', 
                                            boxSizing: 'border-box' 
                                        }}>
                                            <div className="journey-detail-card" style={{ height: '100%' }}>
                                                <div className="journey-grid">
                                                    <div className="journey-img">
                                                        <img src={item.image} alt={item.title} />
                                                    </div>
                                                    <div className="journey-text">
                                                        <h3 className="highlight">{item.title}</h3>
                                                        <p>{item.text}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
            )}

            {/* Excellence Commitment */}
            <section className="excellence section-padding dark-bg">
                <div className="container text-center">
                    <h2 className="section-title-white1">Our Commitment To Excellence</h2>
                    <p className="white opacity-7" style={{ paddingTop: "20px" }}>Delivering precision machining solutions with accuracy, quality standards, <br />and dependable execution.</p>

                    <div className="excellence-grid text-left">
                        <div className="excellence-card">
                            <img src="https://sakthivel.ascentbw.com/Files/About/Icon1.png" alt="Precision Machining" className="excellence-icon" />
                            <div className="card-header">
                                <span className="card-num">01</span>
                                <h4>Precision CNC Machining</h4>
                            </div>
                            <div className="card-line"></div>
                            <p>We deliver accurate CNC and VMC machining solutions with strict dimensional tolerances for industrial applications.</p>
                        </div>
                        <div className="excellence-card">
                            <img src="https://sakthivel.ascentbw.com/Files/About/Icon2.png" alt="Quality Manufacturing" className="excellence-icon" />
                            <div className="card-header">
                                <span className="card-num">02</span>
                                <h4>Quality-Focused Manufacturing</h4>
                            </div>
                            <div className="card-line"></div>
                            <p>Every component is produced with attention to precision, surface finish, and engineering standards.</p>
                        </div>
                        <div className="excellence-card">
                            <img src="https://sakthivel.ascentbw.com/Files/About/Icon3.png" alt="Reliable Delivery" className="excellence-icon" />
                            <div className="card-header">
                                <span className="card-num">03</span>
                                <h4>Reliable Project Delivery</h4>
                            </div>
                            <div className="card-line"></div>
                            <p>We ensure timely execution of machining projects to support uninterrupted production processes.</p>
                        </div>
                        <div className="excellence-card">
                            <img src="https://sakthivel.ascentbw.com/Files/About/Icon4.png" alt="Industrial Expertise" className="excellence-icon" />
                            <div className="card-header">
                                <span className="card-num">04</span>
                                <h4>Industrial Expertise</h4>
                            </div>
                            <div className="card-line"></div>
                            <p>With hands-on experience in machining various metals and materials, we provide dependable engineering solutions.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="faq section-padding">
                <div className="container">
                    <div className="text-center mb-60">
                        <h2 className="section-title">Our Frequently Asked Questions</h2>
                        <p>Find answers to common queries about our products and services.</p>
                    </div>

                    <div className="faq-grid">
                        {faqs.map((faq, idx) => (
                            <div
                                key={idx}
                                className={`faq-item ${activeFaq === idx ? 'active' : ''}`}
                                onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                            >
                                <div className="faq-header">
                                    <h4>{faq.q}</h4>
                                    <span className="toggle-icon"></span>
                                </div>
                                <div className="faq-body">
                                    <p>{faq.a}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FullAbout;